import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

// Validation schema
const employeeSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: z.string().min(1, 'Last name is required').max(50, 'Last name must be less than 50 characters'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  phone: z.string().max(20, 'Phone must be less than 20 characters').optional().or(z.literal('')),
  employeeId: z.string().min(1, 'Employee ID is required').max(50, 'Employee ID must be less than 50 characters'),
  departmentId: z.string().optional().or(z.literal('')),
  branchId: z.string().optional().or(z.literal('')),
  jobTitle: z.string().max(100, 'Job title must be less than 100 characters').optional().or(z.literal('')),
  hireDate: z.string().optional().or(z.literal('')),
  isActive: z.boolean(),
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

interface SelectOption {
  id: string;
  name: string;
}

export default function EmployeeFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEditMode = Boolean(id);

  // Form setup
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      employeeId: '',
      departmentId: '',
      branchId: '',
      jobTitle: '',
      hireDate: '',
      isActive: true,
    },
  });

  const isActive = watch('isActive');

  // Fetch existing employee for edit mode
  const { data: employee, isLoading: employeeLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: async () => {
      const response = await api.get(`/employees/${id}`);
      return response.data.data;
    },
    enabled: isEditMode,
  });

  // Fetch dropdown options
  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await api.get('/departments');
      return response.data.data || [];
    },
  });

  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await api.get('/branches');
      return response.data.data || [];
    },
  });

  // Populate form for edit mode
  useEffect(() => {
    if (employee && isEditMode) {
      reset({
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.email || '',
        phone: employee.phone || '',
        employeeId: employee.employeeId || '',
        departmentId: employee.departmentId || employee.department?.id || '',
        branchId: employee.branchId || employee.branch?.id || '',
        jobTitle: employee.jobTitle || '',
        hireDate: employee.hireDate ? employee.hireDate.split('T')[0] : '',
        isActive: employee.isActive ?? true,
      });
    }
  }, [employee, isEditMode, reset]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: EmployeeFormData) => {
      const payload: Record<string, unknown> = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        employeeId: data.employeeId,
        isActive: data.isActive,
      };
      if (data.departmentId) payload.departmentId = data.departmentId;
      if (data.branchId) payload.branchId = data.branchId;
      if (data.phone) payload.phone = data.phone;
      if (data.jobTitle) payload.jobTitle = data.jobTitle;
      if (data.hireDate) payload.hireDate = data.hireDate;
      const response = await api.post('/employees', payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      toast({
        title: 'Success',
        description: 'Employee created successfully',
      });
      navigate(`/employees/${data.id || data.data?.id || ''}`);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create employee',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: EmployeeFormData) => {
      const payload: Record<string, unknown> = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        employeeId: data.employeeId,
        isActive: data.isActive,
      };
      if (data.departmentId) payload.departmentId = data.departmentId;
      if (data.branchId) payload.branchId = data.branchId;
      if (data.phone) payload.phone = data.phone;
      if (data.jobTitle) payload.jobTitle = data.jobTitle;
      if (data.hireDate) payload.hireDate = data.hireDate;
      const response = await api.put(`/employees/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employee', id] });
      toast({
        title: 'Success',
        description: 'Employee updated successfully',
      });
      navigate(`/employees/${id}`);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update employee',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: EmployeeFormData) => {
    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEditMode && employeeLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={isEditMode ? `/employees/${id}` : '/employees'}>
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{isEditMode ? 'Edit Employee' : 'Add New Employee'}</h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update employee information' : 'Create a new employee record'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      {...register('firstName')}
                      error={errors.firstName?.message}
                      placeholder="e.g., John"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      {...register('lastName')}
                      error={errors.lastName?.message}
                      placeholder="e.g., Doe"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      error={errors.email?.message}
                      placeholder="e.g., john.doe@company.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      {...register('phone')}
                      error={errors.phone?.message}
                      placeholder="e.g., +1 (555) 123-4567"
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Employment Information */}
            <Card>
              <CardHeader>
                <CardTitle>Employment Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="employeeId">Employee ID *</Label>
                    <Input
                      id="employeeId"
                      {...register('employeeId')}
                      error={errors.employeeId?.message}
                      placeholder="e.g., EMP-001"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="jobTitle">Job Title</Label>
                    <Input
                      id="jobTitle"
                      {...register('jobTitle')}
                      error={errors.jobTitle?.message}
                      placeholder="e.g., Software Engineer"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="departmentId">Department</Label>
                    <select
                      id="departmentId"
                      {...register('departmentId')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select department...</option>
                      {(departments as SelectOption[] || []).map((dept) => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="branchId">Branch</Label>
                    <select
                      id="branchId"
                      {...register('branchId')}
                      className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select branch...</option>
                      {(branches as SelectOption[] || []).map((branch) => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hireDate">Hire Date</Label>
                    <Input
                      id="hireDate"
                      type="date"
                      {...register('hireDate')}
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="isActive">Active Status</Label>
                    <p className="text-sm text-muted-foreground">
                      {isActive ? 'Employee can be assigned assets' : 'Employee cannot be assigned new assets'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('isActive')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Button type="submit" className="w-full" disabled={isPending}>
                    {isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {isEditMode ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {isEditMode ? 'Update Employee' : 'Create Employee'}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(isEditMode ? `/employees/${id}` : '/employees')}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Help Text */}
            <Card>
              <CardContent className="p-6">
                <h4 className="font-medium mb-2">Tips</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Employee IDs should be unique</li>
                  <li>• Email is used for notifications</li>
                  <li>• Inactive employees cannot receive assets</li>
                  <li>• Department helps organize employees</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
